// features/orders/surchargePermissions.ts
//
// Ai được thêm/xoá phát sinh trên một đơn. Bản sao của cổng phía server
// (`AllowOrDept("order.surcharge.write", …)` + quy tắc chủ dòng trong
// SurchargesService.Remove) — server vẫn là nơi quyết định; ở đây chỉ để nút
// không hiện ra rồi 403 vào mặt người bấm.

import { ACTION } from "@/lib/auth/actions";
import { deptWriteDoorClosed } from "@/lib/auth/dept-door";
import { useAuthV2 } from "@/lib/authv2/store";
import type { SurchargeGroup, SurchargeCatalogItem } from "@/features/products/constants";

/**
 * Phòng gặp phát sinh ngay tại ca chụp: lễ tân chốt thêm ở quầy, photo và
 * makeup phát hiện đổi đồ / làm tóc giữa buổi. Họ mang role `staff` — role
 * dùng chung với các phòng không dính tới tiền — nên quyền nằm ở phòng ban,
 * không mở rộng `order.surcharge.write` trong ma trận role.
 * Khớp `surchargeWriteDepts` ở backend/internal/core/order/surcharges_handler.go.
 */
const SURCHARGE_WRITE_DEPTS = new Set([
  "reception",
  "photo_idphoto",
  "photo_profile",
  "photo_corporate",
  "makeup",
]);

type SurchargeUser = { role?: string | null; department?: string | null } | null | undefined;

/**
 * Giữ lại các mục thuộc nhóm người này được thấy; thứ tự bảng giá không đổi.
 *
 * `groups` do MÁY CHỦ cấp (`visibleGroups` của GET /surcharge-catalog, QĐ
 * 0114) — giao diện không còn giữ bản chép bản đồ phòng→nhóm. Danh sách rỗng
 * (chưa đọc được bảng giá) → không mục nào lọt: không có bảng giá thì cũng
 * không có gì để chọn.
 */
export function filterSurchargesByGroup<T extends Pick<SurchargeCatalogItem, "group">>(
  items: T[],
  groups: SurchargeGroup[],
): T[] {
  if (groups.length >= 3) return items;
  return items.filter((it) => groups.includes(it.group));
}

/** Người dùng có được thêm phát sinh không (không reactive). */
export function canAddSurcharge(user: SurchargeUser, permissions: Set<string>): boolean {
  if (!user) return false;
  if (permissions.has(ACTION.OrderSurchargeWrite)) return true;
  if (deptWriteDoorClosed(user.role)) return false;
  return SURCHARGE_WRITE_DEPTS.has((user.department ?? "").trim());
}

/** Bản reactive của canAddSurcharge — dùng trong component. */
export function useCanAddSurcharge(): boolean {
  return useAuthV2((s) => canAddSurcharge(s.currentStaff, s.permissions));
}

/**
 * Người dùng có được NÂNG GÓI không (HRS-063). Cùng đúng bộ người với thêm
 * phát sinh: 5 phòng cơ sở đứng bán tại ca, cộng người giữ quyền role.
 * Khớp cổng `AllowOrDept("order.package_upgrade.write", surchargeWriteDepts...)`
 * ở backend/internal/core/order/package_upgrade_handler.go.
 *
 * Dùng lại `SURCHARGE_WRITE_DEPTS` chứ không chép danh sách phòng lần thứ hai:
 * hai bản sao là hai đường trôi khác nhau, và bên trôi sau sẽ là bên chặn nhầm
 * người đang đứng bán.
 */
export function canUpgradePackage(user: SurchargeUser, permissions: Set<string>): boolean {
  if (!user) return false;
  if (permissions.has(ACTION.OrderPackageUpgradeWrite)) return true;
  if (deptWriteDoorClosed(user.role)) return false;
  return SURCHARGE_WRITE_DEPTS.has((user.department ?? "").trim());
}

/** Bản reactive của canUpgradePackage — dùng trong component. */
export function useCanUpgradePackage(): boolean {
  return useAuthV2((s) => canUpgradePackage(s.currentStaff, s.permissions));
}

/**
 * Người dùng có quyền duyệt/từ chối phát sinh chờ duyệt và giải quyết yêu cầu
 * xoá không (`order.surcharge.approve` — admin/giám đốc/quản lý/kế toán).
 * Server vẫn gác route; đây chỉ để giấu nút với người không có quyền.
 */
export function useCanApproveSurcharge(): boolean {
  return useAuthV2((s) => s.permissions.has(ACTION.OrderSurchargeApprove));
}

/**
 * Quyền xoá-mọi-dòng (`order.surcharge.delete` — admin/giám đốc/kế toán).
 * Người này khi xoá BẮT BUỘC kèm lý do (server trả 400 nếu thiếu), nên UI
 * phải hỏi lý do trước khi gọi DELETE.
 */
export function useCanDeleteAnySurcharge(): boolean {
  return useAuthV2((s) => s.permissions.has(ACTION.OrderSurchargeDelete));
}

/**
 * Người dùng có được xoá dòng phát sinh `row` không.
 *
 * Có `order.surcharge.delete` (admin/giám đốc/kế toán) thì xoá được mọi dòng.
 * Còn lại chỉ xoá dòng do chính mình thêm: xoá một phát sinh là xoá doanh thu
 * đã ghi nhận và không để lại vết audit nào, nên phòng chụp sửa lỗi của mình
 * thì được, xoá dòng kế toán ghi thì không.
 *
 * `viewerId` phải là casdoor sub (`useAuthV2.casdoorSub`), không phải
 * `currentStaff.id`: server đóng dấu `added_by_id` từ `auth.User.ID` — chính
 * là sub — nên so với staff id sẽ không khớp dòng nào và mọi nút xoá biến mất.
 *
 * Dòng tạo trước migration 0057 không có `addedById` — chỉ nhóm đặc quyền xoá
 * được, đúng như hành vi trước khi có quy tắc này.
 */
export function canDeleteSurchargeRow(
  user: SurchargeUser,
  permissions: Set<string>,
  viewerId: string | null | undefined,
  row: { addedById?: string | null },
): boolean {
  if (!user) return false;
  if (permissions.has(ACTION.OrderSurchargeDelete)) return true;
  if (!canAddSurcharge(user, permissions)) return false;
  const owner = (row.addedById ?? "").trim();
  return !!owner && !!viewerId && owner === viewerId;
}

/**
 * Người dùng có đổi được ƯU ĐÃI SET THÊM của dòng `row` không (QĐ 0204, CEO
 * chốt câu 8b) — MIRROR của rào máy chủ `mayPatchPackageDiscount`.
 *
 * Hai cửa, cố ý khác nhau:
 *   có `order.surcharge.delete` → mọi lúc (đường gỡ khi quầy lỡ quá ngày)
 *   chủ dòng                    → cùng NGÀY LỊCH VN với lúc thêm dòng
 *
 * "Trong ngày" chứ không phải "trong 24 giờ": sổ quầy chốt theo ngày. Mirror ở
 * đây chỉ để KHÔNG hiện nút rồi bấm xong ăn 403; máy chủ vẫn là chốt cuối, và
 * nó còn một rào nữa mà client không đo được (đơn đã có khoản thu sau đó).
 */
export function canPatchPackageDiscountRow(
  user: SurchargeUser,
  permissions: Set<string>,
  viewerId: string | null | undefined,
  row: { addedById?: string | null; addedAt?: string | null; type?: string | null; status?: string | null },
  now: Date = new Date(),
): boolean {
  if (!user) return false;
  // Chỉ dòng GÓI đang tính tiền. Dòng chờ duyệt chưa phải tiền khách nợ.
  if ((row.type ?? "") !== "package") return false;
  if ((row.status ?? "active") !== "active") return false;
  if (permissions.has(ACTION.OrderSurchargeDelete)) return true;
  if (!canAddSurcharge(user, permissions)) return false;
  const owner = (row.addedById ?? "").trim();
  if (!owner || !viewerId || owner !== viewerId) return false;
  if (!row.addedAt) return false;
  return cungNgayVN(new Date(row.addedAt), now);
}

/** Hai mốc có rơi vào cùng một ngày lịch Việt Nam không. */
function cungNgayVN(a: Date, b: Date): boolean {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Ho_Chi_Minh",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return fmt.format(a) === fmt.format(b);
}

/** Vị từ "đổi được ưu đãi dòng này" đã gắn sẵn người đang đăng nhập. */
export function useCanPatchPackageDiscount(): (row: {
  addedById?: string | null;
  addedAt?: string | null;
  type?: string | null;
  status?: string | null;
}) => boolean {
  const staff = useAuthV2((s) => s.currentStaff);
  const permissions = useAuthV2((s) => s.permissions);
  const viewerId = useAuthV2((s) => s.casdoorSub);
  return (row) => canPatchPackageDiscountRow(staff, permissions, viewerId, row);
}

/**
 * Vị từ "xoá được dòng này" đã gắn sẵn người đang đăng nhập. Trả về hàm thay
 * vì boolean vì mỗi dòng có chủ khác nhau — component gọi cho từng dòng.
 */
export function useCanDeleteSurchargeRow(): (row: { addedById?: string | null }) => boolean {
  const staff = useAuthV2((s) => s.currentStaff);
  const permissions = useAuthV2((s) => s.permissions);
  const viewerId = useAuthV2((s) => s.casdoorSub);
  return (row) => canDeleteSurchargeRow(staff, permissions, viewerId, row);
}
